'use client';

import React, { useEffect, useState, useRef } from 'react';

type MotionTag = 'div' | 'span' | 'section' | 'article' | 'header' | 'footer' | 'main' | 'aside' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'nav';

type SmoothRevealProps<T extends MotionTag = 'div'> = {
  as?: T;
  /**
   * If true, element is visible immediately (no initial="hidden")
   * Use for LCP-critical elements like hero H1 to prevent render delay
   * Animation still plays but content is visible on first paint
   */
  immediate?: boolean;
  /**
   * If true, uses CSS-only animations (no Framer Motion)
   * Useful for above-the-fold content that needs to render immediately
   */
  cssOnly?: boolean;
  variant?: 'fade' | 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right';
  distance?: number;
  delay?: number;
  margin?: string;
  once?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  [key: string]: any;
};

// CSS-only animation styles
const cssAnimationStyles = `
  @keyframes smoothRevealFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes smoothRevealFadeUp {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes smoothRevealFadeDown {
    from {
      opacity: 0;
      transform: translateY(-24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes smoothRevealFadeLeft {
    from {
      opacity: 0;
      transform: translateX(24px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes smoothRevealFadeRight {
    from {
      opacity: 0;
      transform: translateX(-24px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .smooth-reveal-css {
    opacity: 0;
  }
  
  .smooth-reveal-css-visible {
    opacity: 1;
    animation: smoothRevealFadeUp 0.6s ease-out forwards;
  }
  
  .smooth-reveal-css-fade {
    animation: smoothRevealFadeIn 0.6s ease-out forwards;
  }
  
  .smooth-reveal-css-fade-up {
    animation: smoothRevealFadeUp 0.6s ease-out forwards;
  }
  
  .smooth-reveal-css-fade-down {
    animation: smoothRevealFadeDown 0.6s ease-out forwards;
  }
  
  .smooth-reveal-css-fade-left {
    animation: smoothRevealFadeLeft 0.6s ease-out forwards;
  }
  
  .smooth-reveal-css-fade-right {
    animation: smoothRevealFadeRight 0.6s ease-out forwards;
  }
`;

// Framer Motion removed - using CSS-only animations for better performance

export function SmoothReveal<T extends MotionTag = 'div'>({
  as = 'div' as T,
  variant = 'fade-up',
  distance = 24,
  delay = 0,
  margin = '-50px',
  once = true,
  disabled = false,
  immediate = false,
  cssOnly = false,
  className = '',
  children,
  dangerouslySetInnerHTML,
  ...rest
}: SmoothRevealProps<T>) {
  const [isVisible, setIsVisible] = useState(immediate);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // OPTIMIZATION: Always use CSS-only animations for better performance (removed Framer Motion)
  // CSS-only IntersectionObserver for scroll-triggered animations
  useEffect(() => {
    // Skip observer setup if immediate (show right away) or disabled
    if (immediate || disabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once && observerRef.current) {
              observerRef.current.disconnect();
            }
          }
        });
      },
      {
        rootMargin: margin,
        threshold: 0.1,
      }
    );

    observerRef.current = observer;

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [immediate, margin, once, disabled]);

  // CSS-only version for immediate render (Framer Motion removed for performance)
  const Tag = as;
  const variantClass = `smooth-reveal-css-${variant}`;
  const combinedClassName = `${className} ${isVisible ? variantClass : 'smooth-reveal-css'}`.trim();
  const style = {
    animationDelay: `${delay}ms`,
    ...(rest.style || {}),
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: cssAnimationStyles }} />
      <Tag
        ref={(el) => {
          elementRef.current = el as HTMLElement;
          if (rest.ref) {
            if (typeof rest.ref === 'function') {
              rest.ref(el);
            } else {
              (rest.ref as React.MutableRefObject<HTMLElement | null>).current = el;
            }
          }
        }}
        className={combinedClassName}
        style={style}
        {...(rest as any)}
      >
        {children}
        {dangerouslySetInnerHTML && (
          <div dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}
      </Tag>
    </>
  );
}
